'use client'

import { useHeaderTheme } from '@/providers/HeaderTheme'
import React, { useEffect } from 'react'

import type { Page } from '@/payload-types'

import { CMSLink } from '@/components/Link'
import { Media } from '@/components/Media'
import InfoTag from '@/components/ui/InfoTag'
import { getPosterUrl } from '@/utilities/getPosterUrl'

export const VideoHero: React.FC<Page['hero']> = ({ videoHero, settings: _settings }) => {
  const { setHeaderTheme } = useHeaderTheme()

  useEffect(() => {
    setHeaderTheme('dark')
  })

  if (!videoHero) return null

  const { infotag, title, subtitle, videoFile, videoUrl, posterImage, links } = videoHero
  const hasInfotag = Boolean(infotag)

  const getHeightClass = () => {
    // Default to full screen height since height setting is not available in heroFields
    return 'h-dvh'
  }

  return (
    <div
      className={`relative -mt-[10.4rem] flex items-center justify-center text-white ${getHeightClass()}`}
      data-theme="dark"
    >
      <div className="container mb-8 z-10 relative flex items-center justify-center">
        <div className="max-w-[36.5rem] md:text-center">
          {hasInfotag && (
            <InfoTag as="h1" size="md" className="mb-4">
              {infotag}
            </InfoTag>
          )}
          {title &&
            (hasInfotag ? (
              <span className="block text-4xl md:text-6xl font-bold mb-6 uppercase">{title}</span>
            ) : (
              <h1 className="text-4xl md:text-6xl font-bold mb-6 uppercase">{title}</h1>
            ))}
          {subtitle && <p className="text-xl md:text-2xl mb-6">{subtitle}</p>}
          {Array.isArray(links) && links.length > 0 && (
            <div className="flex md:justify-center gap-4">
              {links.map(({ link }, i) => {
                if (!link) return null

                return (
                  <CMSLink
                    key={i}
                    {...link}
                    newTab={link.openInNewTab}
                    appearance="external"
                    size="md"
                    externalColor="brand"
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>
      <div className="absolute inset-0 select-none">
        {/* Video Background */}
        {videoFile && typeof videoFile === 'object' ? (
          <video
            className="w-full h-full object-cover -z-10"
            autoPlay
            muted
            loop
            playsInline
            poster={getPosterUrl(posterImage)}
          >
            <source src={videoFile.url || ''} type="video/mp4" />
          </video>
        ) : videoUrl ? (
          <iframe
            className="w-full h-full object-cover -z-10"
            src={videoUrl}
            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
        ) : posterImage ? (
          <Media
            fill
            imgClassName="-z-10 object-cover"
            priority
            fetchPriority="high"
            resource={posterImage}
            size="100vw"
          />
        ) : null}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/35 to-transparent" />
      </div>
    </div>
  )
}
